import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { signIn, isUserAdmin, testAuthEndpoint, testLogin, emergencyLogin } from "../../supabase";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for mobile screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);
  

const handleLogin = async (e) => {
  e.preventDefault();
  console.log('🔄 Starting login process');
  
  setLoading(true);
  setError("");

  try {
    console.log('🔄 Authenticating user with:', { email });
    
    // Try the enhanced signIn function
    const result = await signIn(email, password);
    
    console.log('📊 Authentication result:', result);
    
    if (!result.success) {
      // If normal login failed, try emergency login
      console.log('🚨 Normal login failed, trying emergency login...');
      
      const emergencyResult = await emergencyLogin(email, password);
      if (emergencyResult.success) {
        result.success = true;
        result.user = emergencyResult.data;
      } else {
        throw new Error(result.error || "Login failed");
      }
    }

    console.log('✅ Login successful:', result.user?.email || 'User authenticated');
    
    // Check user's role and redirect accordingly
    console.log('🔍 Checking user role...');
    const isAdmin = await isUserAdmin(result.user.id);
    
    // Force page refresh to reload auth state
    setTimeout(() => {
      if (isAdmin) {
        console.log('✅ Admin user - redirecting to research dashboard');
        window.location.href = "/research";
      } else {
        console.log('✅ Patient user - redirecting to patient dashboard');
        window.location.href = "/dashboard";
      }
    }, 500);

  } catch (err) {
    console.error('❌ Login error:', err);
    
    setError(err.message || "Login failed. Please try again.");
    setLoading(false);
  }
};

  return (
    <div style={{ 
      minHeight: '100vh', 
      backgroundColor: 'white', 
      width: '100%',
      position: 'relative',
      margin: 0,
      padding: 0,
      overflowX: 'hidden',
      display: 'flex'
    }}>
      {/* Main Content */}
      {isMobile ? (
        // MOBILE LAYOUT - Form only
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 24px 40px',
          width: '100%',
          minHeight: '100vh',
          backgroundColor: 'white'
        }}>
          <div style={{
            width: '100%',
            maxWidth: '400px',
            textAlign: 'left'
          }}>
            {/* Back Button */}
            <div style={{ 
              marginBottom: '24px'
            }}>
              <a href="/" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#4285f4',
                textDecoration: 'none',
                fontSize: '14px',
                fontWeight: '500',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                backgroundColor: '#ffffff',
                transition: 'all 0.2s ease',
                fontFamily: 'Roboto, Arial, sans-serif'
              }}>
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                </svg>
                Back to Home
              </a>
            </div>

            <h1 style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '8px',
              fontFamily: 'Product Sans, Arial, sans-serif',
              margin: '0 0 8px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              Welcome back
              <span style={{ fontSize: '24px' }}>👋</span>
            </h1>
            
            <p style={{
              fontSize: '16px',
              color: '#718096',
              marginBottom: '32px',
              fontFamily: 'Roboto, Arial, sans-serif',
              margin: '0 0 32px 0'
            }}>
              Please enter your login info
            </p>

            <form onSubmit={handleLogin} style={{ width: '100%' }}>
              <div style={{ marginBottom: '20px' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Roboto, Arial, sans-serif',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '24px' }}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    fontSize: '16px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Roboto, Arial, sans-serif',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
              </div>

              {error && (
                <div style={{ 
                  marginBottom: '24px',
                  padding: '12px 16px',
                  backgroundColor: '#fed7d7',
                  border: '1px solid #feb2b2',
                  borderRadius: '12px',
                  color: '#c53030',
                  fontSize: '14px',
                  fontFamily: 'Roboto, Arial, sans-serif'
                }}>
                  {error}
                </div>
              )}

              <button 
                type="submit"
                disabled={!email || !password || loading}
                style={{
                  width: '100%',
                  padding: '16px 24px',
                  backgroundColor: loading || (!email || !password) ? '#cccccc' : '#4285f4',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading || (!email || !password) ? 'not-allowed' : 'pointer',
                  fontFamily: 'Roboto, Arial, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  outline: 'none',
                  boxShadow: loading || (!email || !password) ? 'none' : '0 2px 8px rgba(66, 133, 244, 0.3)'
                }}
              >
                {loading && (
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid #ffffff',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                )}
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          </div>
        </div>
      ) : (
        // DESKTOP LAYOUT - Logo left, Form right
        <div style={{ 
          display: 'flex',
          minHeight: '100vh',
          backgroundColor: 'white',
          flexDirection: 'row',
          width: '100%'
        }}>
          {/* Logo Section - LEFT SIDE */}
          <div style={{
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #4285f4 0%, #34a853 50%, #1976d2 100%)',
            padding: '40px',
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background decoration */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              right: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 30%, transparent 70%)',
              borderRadius: '50%',
              transform: 'rotate(45deg)'
            }} />
            <div style={{
              position: 'absolute',
              bottom: '-30%',
              left: '-30%',
              width: '150%',
              height: '150%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 50%)',
              borderRadius: '50%'
            }} />
            
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              zIndex: 2,
              position: 'relative'
            }}>
              {/* Logo container with subtle background */}
              <div style={{
                padding: '40px',
                borderRadius: '24px',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(15px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                marginBottom: '24px'
              }}>
                <img 
                  src="/assets/FFWLOGOtransparent.png" 
                  alt="Face Forward Web Logo" 
                  style={{
                    maxWidth: '400px',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2))'
                  }}
                  onError={(e) => {
                    // Fallback if logo not found
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                {/* Fallback text if logo doesn't load */}
                <div style={{
                  display: 'none',
                  fontSize: '80px',
                  fontWeight: '700',
                  color: '#ffffff',
                  fontFamily: 'Product Sans, Arial, sans-serif',
                  textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)'
                }}>
                  FFW
                </div>
              </div>
            </div>
          </div>

          {/* Form Section - RIGHT SIDE */}
          <div style={{
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '80px 60px',
            width: '50%',
            backgroundColor: 'white'
          }}>
            <div style={{
              width: '100%',
              maxWidth: '400px',
              textAlign: 'left'
            }}>
              {/* Back Button */}
              <div style={{ 
                marginBottom: '24px'
              }}>
                <a href="/" style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#4285f4',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#ffffff',
                  transition: 'all 0.2s ease',
                  fontFamily: 'Roboto, Arial, sans-serif'
                }}>
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                  </svg>
                  Back to Home
                </a>
              </div>

              <h1 style={{
                fontSize: '32px',
                fontWeight: '600',
                color: '#2d3748',
                marginBottom: '8px',
                fontFamily: 'Product Sans, Arial, sans-serif',
                margin: '0 0 8px 0',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                Welcome back
                <span style={{ fontSize: '24px' }}>👋</span>
              </h1>
              
              <p style={{
                fontSize: '16px',
                color: '#718096',
                marginBottom: '40px',
                fontFamily: 'Roboto, Arial, sans-serif',
                margin: '0 0 40px 0'
              }}>
                Please enter your login info
              </p>

              <form onSubmit={handleLogin} style={{ width: '100%' }}>
                <div style={{ marginBottom: '20px' }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Roboto, Arial, sans-serif',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                </div>
                
                <div style={{ marginBottom: '24px' }}>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Password"
                    required
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '16px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#ffffff',
                      transition: 'all 0.2s ease',
                      fontFamily: 'Roboto, Arial, sans-serif',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                </div>

                {error && (
                  <div style={{ 
                    marginBottom: '24px',
                    padding: '12px 16px',
                    backgroundColor: '#fed7d7',
                    border: '1px solid #feb2b2',
                    borderRadius: '12px',
                    color: '#c53030',
                    fontSize: '14px',
                    fontFamily: 'Roboto, Arial, sans-serif'
                  }}>
                    {error}
                  </div>
                )}

                <button 
                  type="submit"
                  disabled={!email || !password || loading}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    backgroundColor: loading || (!email || !password) ? '#cccccc' : '#4285f4',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: loading || (!email || !password) ? 'not-allowed' : 'pointer',
                    fontFamily: 'Roboto, Arial, sans-serif',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    outline: 'none',
                    boxShadow: loading || (!email || !password) ? 'none' : '0 2px 8px rgba(66, 133, 244, 0.3)'
                  }}
                >
                  {loading && (
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff40',
                      borderTop: '2px solid #ffffff',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                  )}
                  {loading ? 'Signing in...' : 'Sign in'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
      
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          /* Remove any button overlays completely */
          button {
            position: relative !important;
            overflow: visible !important;
          }
          
          button::before,
          button::after,
          button *::before,
          button *::after {
            content: none !important;
            display: none !important;
          }
          
          /* Mobile styles */
          @media (max-width: 768px) {
            body {
              margin: 0;
              padding: 0;
              overflow-x: hidden;
            }
          }

          /* Focus states */
          input:focus {
            outline: none;
          }
        `
      }} />
    </div>
  );
};

export default Login;